import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Card, EmptyState, PageHeader, StatCard } from "../components/account-ui.tsx";
import { CampaignStatusBadge, FinalPdfBadge, OrderStatusBadge } from "../components/status.tsx";
import {
  CampaignOutlineIcon,
  ChevronRightIcon,
  CreditCardIcon,
  type IconProps,
  MapPinIcon,
  PlusIcon,
  ShoppingBagIcon,
} from "../icons/index.tsx";
import {
  accountSummary,
  effectiveStatus,
  formatDate,
  formatNumbers,
  formatPrices,
  splitName,
} from "../lib/legacy.ts";
import { useAddresses } from "./account.address-book.tsx";
import { useOrdersWithProducts } from "./account.orders.tsx";
import { useProfile } from "./account.tsx";

export const Route = createFileRoute("/account/")({
  component: AccountDashboard,
});

function AccountDashboard() {
  const { data } = useProfile();
  const profile = data?.profile;
  const { orders, campaigns, productFor } = useOrdersWithProducts();
  const { data: addressData } = useAddresses();

  if (!profile) return <div className="h-[100vh]" />;
  const { firstName } = splitName(profile.name);
  const summary = accountSummary(orders ?? [], campaigns ?? []);
  const addresses = addressData?.addresses;
  // A dash until the query lands, so a real zero is never faked.
  const count = (n: number, loaded: boolean) => (loaded ? formatNumbers(n) : "—");
  const roleBadge =
    profile.role === "admin" ? (
      <span className="rounded-full bg-amber-400 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-950">
        Admin
      </span>
    ) : profile.role === "staff" ? (
      <span className="rounded-full bg-slate-300 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-800">
        Staff
      </span>
    ) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {`Welcome back, ${firstName || profile.name}`}
            {roleBadge}
          </span>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Campaigns"
          value={count(summary.campaignCount, campaigns !== undefined)}
          hint="Active and completed"
          Icon={CampaignOutlineIcon}
        />
        <StatCard
          label="Orders"
          value={count(summary.orderCount, orders !== undefined)}
          hint="Placed all time"
          Icon={ShoppingBagIcon}
        />
        <StatCard
          label="Total spend"
          value={orders ? `$${formatPrices(summary.spentDollars)}` : "—"}
          hint="Excludes cancelled and refunded"
          Icon={CreditCardIcon}
        />
        <StatCard
          label="Organisation locations"
          value={count(addresses?.length ?? 0, addresses !== undefined)}
          hint="Locations and billing"
          Icon={MapPinIcon}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card
          title="Recent campaigns"
          action={
            profile.id ? (
              <Link
                to="/app/$customerId/mycampaigns"
                params={{ customerId: profile.id }}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                View all
              </Link>
            ) : null
          }
          bodyClass=""
        >
          {summary.recentCampaigns.length === 0 ? (
            <EmptyState
              Icon={CampaignOutlineIcon}
              title="No campaigns yet"
              message="Your campaigns will appear here once you start one."
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {summary.recentCampaigns.map((c) => (
                <li key={c.id}>
                  <Link
                    to="/app/$customerId/mycampaigns/$campaignId"
                    params={{
                      customerId: c.customer_id ?? c.user_id ?? profile.id,
                      campaignId: c.id,
                    }}
                    className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {c.name || "Untitled campaign"}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {formatNumbers(c.address_count ?? 0) || "0"} addresses · updated{" "}
                        {formatDate(c.updated_at)}
                      </p>
                    </div>
                    <CampaignStatusBadge status={effectiveStatus(c)} />
                    <FinalPdfBadge status={c.final_pdf_status} updatedAt={c.final_pdf_updated_at} />
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-300" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title="Recent orders"
          action={
            <Link
              to="/account/orders"
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              View all
            </Link>
          }
          bodyClass=""
        >
          {summary.recentOrders.length === 0 ? (
            <EmptyState
              Icon={ShoppingBagIcon}
              title="No orders yet"
              message="Approved campaigns you pay for will show up here."
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {summary.recentOrders.map((o) => {
                const product = productFor(o);
                return (
                  <li key={o.id}>
                    <Link
                      to="/account/orders/$code"
                      params={{ code: o.code }}
                      className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-gray-50"
                    >
                      {product?.image ? (
                        <img
                          src={product.image}
                          alt=""
                          width={40}
                          height={40}
                          className="h-10 w-10 shrink-0 rounded-lg border border-gray-200 object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 shrink-0 rounded-lg bg-gray-100" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">{o.code}</p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {formatDate(o.placed_at ?? o.created_at)} ·{" "}
                          {formatNumbers(o.quantity) || "0"} items
                        </p>
                      </div>
                      <OrderStatusBadge status={o.status} />
                      <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                        ${formatPrices(o.total_cents / 100)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <QuickAction
          to="/app/collections/$slug"
          params={{ slug: "all-products" }}
          Icon={CampaignOutlineIcon}
          title="Browse Products"
          description="Choose a product and map your area"
        />
        <QuickAction
          to="/app/$customerId/mycampaigns"
          params={{ customerId: profile.id }}
          Icon={ShoppingBagIcon}
          title="My campaigns"
          description="Pick up where you left off"
        />
        <QuickAction
          to="/account/address-book/$id"
          params={{ id: "add" }}
          Icon={PlusIcon}
          title="Add a location"
          description="Save an organisation location"
        />
      </div>
    </div>
  );
}

function QuickAction({
  to,
  params,
  Icon,
  title,
  description,
}: {
  to: string;
  params: Record<string, string>;
  Icon: (p: IconProps) => ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      params={params}
      className="group flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-primary-300 hover:shadow-md"
    >
      <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-900">{title}</span>
        <span className="block truncate text-xs text-gray-500">{description}</span>
      </span>
      <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-300 group-hover:text-primary-500" />
    </Link>
  );
}
